/**
 * One value that changes too often to keep in React state (a pan/zoom view
 * while dragging, say), owned by one component instance. Readers either
 * select a slice, re-rendering only when it changes, or apply the value
 * straight to the DOM on every change without re-rendering. The value can't
 * be a function: `set` takes a function as an updater, like a state setter.
 */
export interface ValueStore<T> {
    get(): T;
    /** Sets the value, or derives it from the current one. Listeners hear only of a different value. */
    set(next: T | ((current: T) => T)): void;
    subscribe(listener: () => void): () => void;
}

export function createValueStore<T>(initial: T): ValueStore<T> {
    let value = initial;
    const listeners = new Set<() => void>();
    return {
        get: () => value,
        set(next) {
            const resolved = typeof next === 'function' ? (next as (current: T) => T)(value) : next;
            if (Object.is(resolved, value)) {
                return;
            }
            value = resolved;
            for (const listener of listeners) {
                listener();
            }
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}
